import type {
  Company,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  TestConnectionRequest,
  TestConnectionResponse,
  SyncRequest,
  SyncResponse,
  IntegrationConfig,
  CompanyStatus,
} from '../types/company.types';
import { apiClient } from '@/services/apiClient';

// API response type from backend
interface CompanyApiResponse {
  id: string;
  name: string;
  code: string;
  description?: string;
  status: string;
  settings?: {
    timezone?: string;
    currency?: string;
    fiscalYearStart?: string;
  };
  employeeCount: number;
  activeProjects: number;
  hasCostingIntegration?: boolean;
  createdAt: string;
  updatedAt: string;
}

// Map API response to frontend Company type
function mapApiResponseToCompany(apiCompany: CompanyApiResponse): Company {
  return {
    id: apiCompany.id,
    name: apiCompany.name,
    code: apiCompany.code,
    description: apiCompany.description,
    status: apiCompany.status as CompanyStatus,
    productInstances: [],
    hasCostingIntegration: apiCompany.hasCostingIntegration ?? false,
    financialIntegration: null,
    hrIntegration: null,
    activeProjects: apiCompany.activeProjects,
    employeeCount: apiCompany.employeeCount,
    createdAt: new Date(apiCompany.createdAt),
    updatedAt: new Date(apiCompany.updatedAt),
    lastSync: null,
  };
}

export const companyService = {
  // Get all companies from backend API
  async getCompanies(): Promise<Company[]> {
    const apiCompanies = await apiClient.get<CompanyApiResponse[]>('/companies');
    return apiCompanies.map(mapApiResponseToCompany);
  },

  // Get active companies only (for header selector)
  async getActiveCompanies(): Promise<Company[]> {
    const companies = await this.getCompanies();
    return companies.filter((c) => c.status === 'active');
  },

  // Get company by ID from backend API
  async getCompany(id: string): Promise<Company | null> {
    try {
      const apiCompany = await apiClient.get<CompanyApiResponse>(`/companies/${id}`);
      return mapApiResponseToCompany(apiCompany);
    } catch (error) {
      console.error('Failed to get company:', error);
      return null;
    }
  },

  // Create new company via backend API
  async createCompany(data: CreateCompanyRequest): Promise<Company> {
    const apiCompany = await apiClient.post<CompanyApiResponse, CreateCompanyRequest>(
      '/companies',
      data
    );
    return mapApiResponseToCompany(apiCompany);
  },

  // Update company via backend API
  async updateCompany(id: string, data: UpdateCompanyRequest): Promise<Company> {
    const apiCompany = await apiClient.put<CompanyApiResponse, UpdateCompanyRequest>(
      `/companies/${id}`,
      data
    );
    return mapApiResponseToCompany(apiCompany);
  },

  // Delete company via backend API
  async deleteCompany(id: string): Promise<void> {
    await apiClient.delete(`/companies/${id}`);
  },

  // Update integration configuration (placeholder - needs backend endpoint)
  async updateIntegration(
    companyId: string,
    integrationType: 'financial' | 'hr',
    config: Partial<IntegrationConfig>
  ): Promise<IntegrationConfig> {
    // TODO: Implement when backend endpoint is available
    console.warn('updateIntegration not yet implemented with backend');
    const integration: IntegrationConfig = {
      id: `${integrationType}-${companyId}-${Date.now()}`,
      companyId,
      systemType: config.systemType || '',
      systemName: config.systemName || '',
      connectionStatus: config.connectionStatus || 'not_configured',
      apiEndpoint: config.apiEndpoint || null,
      apiKey: config.apiKey || null,
      lastSync: null,
      lastTestDate: null,
      lastTestStatus: null,
      syncFrequency: config.syncFrequency || 'manual',
      autoSync: config.autoSync || false,
      config: config.config || {},
      errorMessage: null,
    };
    return Promise.resolve(integration);
  },

  // Test integration connection (placeholder - needs backend endpoint)
  async testConnection(data: TestConnectionRequest): Promise<TestConnectionResponse> {
    // TODO: Implement when backend endpoint is available
    console.warn('testConnection not yet implemented with backend');
    await new Promise((resolve) => setTimeout(resolve, 1500));
    return Promise.resolve({
      success: true,
      message: 'Connection successful',
      details: {
        systemVersion: '2024.1',
        companyName: 'Test Company',
        availableModules: ['Projects', 'Timesheets', 'Expenses'],
      },
    });
  },

  // Trigger manual sync (placeholder - needs backend endpoint)
  async triggerSync(data: SyncRequest): Promise<SyncResponse> {
    // TODO: Implement when backend endpoint is available
    console.warn('triggerSync not yet implemented with backend');
    await new Promise((resolve) => setTimeout(resolve, 2000));
    return Promise.resolve({
      success: true,
      message: 'Sync completed successfully',
      recordsImported: 45,
      recordsUpdated: 12,
      recordsFailed: 0,
      duration: 2000,
    });
  },
};
